/* eslint-disable catalyst/prefer-array-immutable-methods */
import { NgTemplateOutlet } from "@angular/common";
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  Input,
  numberAttribute,
  OnInit,
  ViewEncapsulation,
  inject,
  signal,
} from "@angular/core";
import {
  faCheck,
  faChevronDown,
  faChevronRight,
  faCircleNotch,
  faMinus,
} from "@fortawesome/free-solid-svg-icons";
import { TreeNode } from "primeng/api";
import { ScrollerModule } from "primeng/scroller";
import { TreeComponent } from "src/prime/tree/tree";
import { StandardIconComponent } from "src/standard/standard-icon/standard-icon.component";
import { find } from "src/standard/ts/dom/find";
import { findSingle } from "src/standard/ts/dom/findSingle";
import { focus } from "src/standard/ts/dom/focus";
import { hasClass } from "src/standard/ts/dom/hasClass";
import { findNextElement } from "src/standard/ts/findNextElement";
import { findPreviousElement } from "src/standard/ts/findPreviousElement";
import { yieldToMainThread } from "src/standard/ts/yieldToMainThread";
import invariant from "tiny-invariant";

@Component({
  standalone: true,
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: "p-treeNode",
  templateUrl: "./tree-node.component.html",
  encapsulation: ViewEncapsulation.None,
  host: {
    class: "p-element",
  },
  imports: [NgTemplateOutlet, ScrollerModule, StandardIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .p-treenode-droppoint-dot {
      position: absolute;
      bottom: -9px;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      border: 2px solid var(--bs-tree-drop-next-color);
      background-color: white;
      transform: translateY(-50%);
      z-index: 2;

      &-start {
        left: 0;
      }

      &-end {
        right: 0;
      }
    }
    .treenode-droppoint-parent-active {
      box-shadow:
        -3px -3px 0px var(--bs-tree-drop-node-color) inset,
        3px 3px 0px var(--bs-tree-drop-node-color) inset !important;
    }

    .treenode-droppoint-item-active-previous {
      box-shadow: 0px 3px 0px var(--bs-tree-drop-previous-color) inset !important;
    }

    .treenode-droppoint-item-active-next {
      box-shadow: inset 0 -3px 0 0 var(--bs-tree-drop-node-color) !important;
    }

    .treenode-droppoint-item-active-next-between {
      box-shadow: inset 0 -3px 0 0 var(--bs-tree-drop-next-color) !important;
    }
  `,
})
export class TreeNodeComponent<TData> implements OnInit {
  static ICON_CLASS: string = "p-treenode-icon ";
  readonly faChevronRight = faChevronRight;
  readonly faChevronDown = faChevronDown;
  readonly faCircleNotch = faCircleNotch;
  readonly faCheck = faCheck;
  readonly faMinus = faMinus;

  protected readonly collapseText = $localize`Collapse`;
  protected readonly expandText = $localize`Expand`;

  protected readonly tree = inject<TreeComponent<TData>>(TreeComponent);

  readonly node = input.required<TreeNode<TData>>();
  parentNode = input<TreeNode<TData> | undefined>(undefined);
  @Input({ transform: booleanAttribute }) root: boolean | undefined;
  @Input({ transform: numberAttribute }) index: number = 0;
  readonly firstChild = input<boolean | undefined, unknown>(undefined, {
    transform: booleanAttribute,
  });
  readonly lastChild = input<boolean | undefined, unknown>(undefined, {
    transform: booleanAttribute,
  });
  @Input({ transform: numberAttribute }) level: number | undefined;
  readonly indentation = input<number>();
  @Input({ transform: numberAttribute }) parentItemSize: number | undefined;
  @Input({ transform: numberAttribute }) childItemSize: number | undefined;
  @Input() loadingMode: string | undefined;

  @Input() ignoreDefaultTabOrder: boolean | undefined = false;

  protected readonly draghoverPrev = signal<boolean | undefined>(undefined);
  protected readonly draghoverNext = signal<boolean | undefined>(undefined);
  protected readonly draghoverNode = signal<boolean | undefined>(undefined);

  readonly ariaSelected = computed(() => {
    if (
      this.tree.selectionMode() === "single" ||
      this.tree.selectionMode() === "multiple"
    ) {
      return this.isSelected();
    }

    return undefined;
  });

  readonly ariaChecked = computed(() => {
    if (this.tree.selectionMode() === "checkbox") {
      this.isSelected();
    }

    return undefined;
  });

  ngOnInit() {
    this.node().parent = this.parentNode();
    const nativeElement = this.tree.el.nativeElement;
    const pDialogWrapper = nativeElement.closest("p-dialog");

    if (this.parentNode() && !pDialogWrapper) {
      this.setAllNodesTabIndexes();
    }
  }

  readonly iconClasses = computed(() => {
    const classes: (string | undefined)[] = [TreeNodeComponent.ICON_CLASS];

    const node = this.node();
    if (node.icon) {
      classes.push(node.icon);
    } else {
      if (node.expanded && node.children?.length) {
        classes.push(node.expandedIcon);
      } else {
        classes.push(node.collapsedIcon);
      }
    }

    return classes.join(" ");
  });

  readonly isNodeLeaf = computed(() => {
    const node = this.node();
    return node.leaf == false ? false : !node.children?.length;
  });

  readonly templateForNode = computed(() => {
    const node = this.node();
    const templateMap = this.tree._templateMap();
    const key = node.type ? node.type : "default";
    const found = templateMap[key];
    return found ?? null;
  });

  readonly canShowDropPointBetween = computed(() => {
    const node = this.node();
    const canShowDropPointBetweenFn = this.tree.canShowDropPointBetweenFn();

    // Use the provided function if available, otherwise fall back to checking children
    if (canShowDropPointBetweenFn) {
      return canShowDropPointBetweenFn(node);
    }

    return false;
  });

  dragPosition = computed(() => {
    return this.draghoverNode()
      ? "node"
      : this.draghoverNext()
        ? "after"
        : this.draghoverPrev()
          ? "before"
          : undefined;
  });

  toggle(event: Event) {
    if (this.node().expanded) {
      this.collapse(event);
    } else {
      this.expand(event);
    }

    event.stopPropagation();
  }

  expand(event: Event) {
    this.node().expanded = true;
    if (this.tree.virtualScroll()) {
      this.tree.updateSerializedValue();
    }
    this.tree.onNodeExpand.emit({
      originalEvent: event,
      node: this.node(),
    });
  }

  collapse(event: Event) {
    this.node().expanded = false;
    if (this.tree.virtualScroll()) {
      this.tree.updateSerializedValue();
    }
    this.tree.onNodeCollapse.emit({
      originalEvent: event,
      node: this.node(),
    });
  }

  onNodeClick(event: MouseEvent) {
    this.tree.onNodeClick(event, this.node());
  }

  onNodeKeydown(event: KeyboardEvent) {
    if (event.key === "Enter" || event.key === "NumpadEnter") {
      this.tree.onNodeClick(event, this.node());
    }
  }

  onNodeTouchEnd() {
    this.tree.onNodeTouchEnd();
  }

  onNodeRightClick(event: MouseEvent) {
    this.tree.onNodeRightClick(event, this.node());
  }

  readonly isSelected = computed(() => {
    const { selectionMode, selectionArray } = this.tree.selectionData();
    if (selectionMode && selectionArray.length) {
      const node = this.node();
      return selectionArray.some(
        (selectedNode) =>
          selectedNode === node ||
          (selectedNode.key === node.key && selectedNode.key !== undefined),
      );
    }

    return false;
  });

  private isSameNode(event: KeyboardEvent) {
    const currentTarget = event.currentTarget as HTMLElement | undefined;
    const target = event.target as HTMLElement | undefined;
    return (
      currentTarget &&
      target &&
      (currentTarget.isSameNode(target) ||
        currentTarget.isSameNode(target.closest('[role="treeitem"]')))
    );
  }

  onDropPoint(event: DragEvent, position: number) {
    event.preventDefault();
    const dragNode = this.tree.dragNode;
    invariant(dragNode);
    const dragNodeIndex = this.tree.dragNodeIndex;
    const dragNodeScope = this.tree.dragNodeScope;
    const isValidDropPointIndex =
      this.tree.dragNodeTree === this.tree
        ? position === 1 || dragNodeIndex !== this.index! - 1
        : true;

    if (
      this.tree.allowDrop(dragNode, this.node(), dragNodeScope, "between") &&
      isValidDropPointIndex
    ) {
      const dropParams = this.createDropPointEventMetadata(position);

      if (this.tree.validateDrop()) {
        this.tree.onNodeDrop.emit({
          originalEvent: event,
          dragNode: dragNode,
          dropNode: this.node(),
          dropPoint: "between",
          index: this.index,
          accept: () => {
            this.processPointDrop(dropParams);
          },
        });
      } else {
        this.processPointDrop(dropParams);
        this.tree.onNodeDrop.emit({
          originalEvent: event,
          dragNode: dragNode,
          dropNode: this.node(),
          dropPoint: "between",
          index: this.index,
        });
      }
    }

    this.draghoverPrev.set(false);
    this.draghoverNext.set(false);
  }

  processPointDrop(
    event: ReturnType<typeof this.createDropPointEventMetadata>,
  ) {
    invariant(this.tree.dragDropService);
    const newNodeList = event.dropNode.parent
      ? (event.dropNode.parent.children ?? [])
      : this.tree.value();
    event.dragNodeSubNodes.splice(event.dragNodeIndex, 1);
    let dropIndex = this.index;

    if (event.position < 0) {
      dropIndex =
        event.dragNodeSubNodes === newNodeList
          ? event.dragNodeIndex > event.index
            ? event.index
            : event.index - 1
          : event.index;
      newNodeList.splice(dropIndex, 0, event.dragNode);
    } else {
      dropIndex = newNodeList.length;
      newNodeList.push(event.dragNode);
    }

    this.tree.dragDropService.stopDrag({
      node: event.dragNode,
      subNodes: event.dropNode.parent
        ? event.dropNode.parent.children
        : this.tree.value(),
      index: event.dragNodeIndex,
    });
  }

  createDropPointEventMetadata(position: number) {
    const { dragNode, dragNodeIndex, dragNodeSubNodes } = this.tree;
    invariant(dragNode);
    invariant(typeof dragNodeIndex === "number" && dragNodeIndex >= 0);
    invariant(dragNodeSubNodes);

    return {
      dragNode: dragNode,
      dragNodeIndex: dragNodeIndex,
      dragNodeSubNodes: dragNodeSubNodes,
      dropNode: this.node(),
      index: this.index,
      position: position,
    };
  }

  onDropPointDragOver(event: DragEvent) {
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }

    event.preventDefault();
  }

  onDropPointDragEnter(event: Event, position: number) {
    const dragNode = this.tree.dragNode;
    invariant(dragNode);
    if (
      this.tree.allowDrop(
        dragNode,
        this.node(),
        this.tree.dragNodeScope,
        "between",
      )
    ) {
      if (position < 0) {
        this.draghoverPrev.set(true);
      } else {
        this.draghoverNext.set(true);
      }
    }
  }

  onDropPointDragLeave(event: Event) {
    this.draghoverPrev.set(false);
    this.draghoverNext.set(false);
  }

  onDragStart(event: DragEvent) {
    if (this.tree.draggableNodes() && this.node().draggable !== false) {
      invariant(this.tree.dragDropService);

      if (event.dataTransfer) {
        event.dataTransfer.setData("text", "data");
        event.dataTransfer.setDragImage(document.createElement("div"), 0, 0);
      }

      const nodeParent = this.node().parent;
      this.tree.dragDropService.startDrag({
        tree: this,
        node: this.node(),
        subNodes: nodeParent ? nodeParent.children : this.tree.value(),
        index: this.index,
        scope: this.tree.draggableScope(),
      });
    } else {
      event.preventDefault();
    }
  }

  onDragStop(event: Event) {
    invariant(this.tree.dragDropService);
    const nodeParent = this.node().parent;
    this.tree.dragDropService.stopDrag({
      node: this.node(),
      subNodes: nodeParent ? nodeParent.children : this.tree.value(),
      index: this.index,
    });
  }

  onDropNodeDragOver(event: DragEvent) {
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }

    if (this.tree.droppableNodes()) {
      event.preventDefault();
      event.stopPropagation();

      // Calculate mouse position within the node
      const currentTarget = event.currentTarget as HTMLElement;
      const rect = currentTarget.getBoundingClientRect();
      const relativeY = event.clientY - rect.top;
      const nodeHeight = rect.height;
      const topZone = nodeHeight * 0.2; // First 20%
      const bottomZone = nodeHeight * 0.8; // Last 20%

      if (relativeY <= topZone && this.firstChild() && this.parentNode()) {
        this.draghoverPrev.set(true);
        this.draghoverNext.set(false);
        this.draghoverNode.set(false);
      } else if (
        relativeY >= bottomZone &&
        relativeY <= nodeHeight &&
        this.canShowDropPointBetween() &&
        this.parentNode()
      ) {
        this.draghoverPrev.set(false);
        this.draghoverNext.set(true);
        this.draghoverNode.set(false);
      } else {
        this.draghoverPrev.set(false);
        this.draghoverNext.set(false);
        this.draghoverNode.set(true);
      }
    }

    // Emit the drag over event
    this.tree.onNodeDragOver.emit({
      originalEvent: event as DragEvent,
      dropNode: this.node(),
      dropPoint: this.dragPosition(),
    });
  }

  isExternalDataBeingDragged(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.items || []).length > 0;
  }

  onDropNode(event: DragEvent) {
    if (this.tree.droppableNodes() && this.node().droppable !== false) {
      const dragNode = this.tree.dragNode;

      const dropPoint = this.dragPosition();
      if (this.tree.allowDrop(dragNode, this.node(), this.tree.dragNodeScope)) {
        const dropParams = this.createDropNodeEventMetadata();

        if (this.tree.validateDrop()) {
          this.tree.onNodeDrop.emit({
            originalEvent: event,
            dragNode: dragNode,
            dropNode: this.node(),
            dropPoint: dropPoint,
            index: this.index,
            accept: () => {
              this.processNodeDrop(dropParams);
            },
          });
        } else {
          this.processNodeDrop(dropParams);
          this.tree.onNodeDrop.emit({
            originalEvent: event,
            dragNode: dragNode,
            dropNode: this.node(),
            dropPoint: dropPoint,
            index: this.index,
          });
        }
      } else if (
        this.tree.isExternalDataDragDropEnabled() &&
        this.isExternalDataBeingDragged(event)
      ) {
        this.tree.onNodeDrop.emit({
          originalEvent: event,
          dragNode: dragNode,
          dropNode: this.node(),
          dropPoint: dropPoint,
          index: this.index,
        });
      }
    }

    event.preventDefault();
    event.stopPropagation();
    this.draghoverNode.set(false);

    // Also clear the drop point highlights
    this.draghoverPrev.set(false);
    this.draghoverNext.set(false);
  }

  private createDropNodeEventMetadata() {
    const { dragNode, dragNodeIndex, dragNodeSubNodes } = this.tree;
    invariant(dragNode);
    invariant(dragNodeSubNodes);
    invariant(typeof dragNodeIndex === "number" && dragNodeIndex >= 0);

    return {
      dragNode: dragNode,
      dragNodeIndex: dragNodeIndex,
      dragNodeSubNodes: dragNodeSubNodes,
      dropNode: this.node(),
    };
  }

  processNodeDrop(event: ReturnType<typeof this.createDropNodeEventMetadata>) {
    invariant(this.tree.dragDropService);
    const dragNodeIndex = event.dragNodeIndex;
    event.dragNodeSubNodes.splice(dragNodeIndex, 1);

    if (event.dropNode.children) {
      event.dropNode.children.push(event.dragNode);
    } else {
      event.dropNode.children = [event.dragNode];
    }

    this.tree.dragDropService.stopDrag({
      node: event.dragNode,
      subNodes: event.dropNode.parent
        ? event.dropNode.parent.children
        : this.tree.value(),
      index: dragNodeIndex,
    });
  }

  onDropNodeDragEnter(event: Event) {
    const isExternalDataBeingDragged =
      this.tree.isExternalDataDragDropEnabled() &&
      this.isExternalDataBeingDragged(event as DragEvent);
    if (
      (this.tree.droppableNodes() &&
        this.node().droppable !== false &&
        this.tree.allowDrop(
          this.tree.dragNode!,
          this.node(),
          this.tree.dragNodeScope,
        )) ||
      isExternalDataBeingDragged
    ) {
      // Calculate mouse position within the node
      const dragEvent = event as DragEvent;
      const currentTarget = dragEvent.currentTarget as HTMLElement;
      const rect = currentTarget.getBoundingClientRect();
      const relativeY = dragEvent.clientY - rect.top;
      const nodeHeight = rect.height;
      const topZone = nodeHeight * 0.2; // First 20%
      const bottomZone = nodeHeight * 0.8; // Last 20%

      // Set appropriate hover state based on mouse position
      if (relativeY <= topZone && this.firstChild() && this.parentNode()) {
        this.draghoverPrev.set(true);
        this.draghoverNext.set(false);
        this.draghoverNode.set(false);
      } else if (
        relativeY >= bottomZone &&
        relativeY <= nodeHeight &&
        this.canShowDropPointBetween() &&
        this.parentNode()
      ) {
        this.draghoverPrev.set(false);
        this.draghoverNext.set(true);
        this.draghoverNode.set(false);
      } else {
        this.draghoverPrev.set(false);
        this.draghoverNext.set(false);
        this.draghoverNode.set(true);
      }
    }

    // Emit the drag enter event
    this.tree.onNodeDragEnter.emit({
      originalEvent: event as DragEvent,
      dropNode: this.node(),
      dropPoint: this.dragPosition(),
    });
  }

  onDropNodeDragLeave(event: DragEvent) {
    if (this.tree.droppableNodes()) {
      const currentTarget = event.currentTarget as HTMLElement | undefined;
      invariant(currentTarget);
      const rect = currentTarget.getBoundingClientRect();

      //removed condition because it was causing issues when moving between child elements, sometimes the drag leave would not clear properly correctly
      this.draghoverNode.set(false);
      this.draghoverPrev.set(false);
      this.draghoverNext.set(false);
    }
    // Emit the drag leave event
    this.tree.onNodeDragLeave.emit({
      originalEvent: event as DragEvent,
      dropNode: this.node(),
      dropPoint: this.dragPosition(),
    });
  }

  onKeyDown(event: KeyboardEvent) {
    const contextMenu = this.tree.contextMenu();
    if (
      !this.isSameNode(event) ||
      (contextMenu &&
        contextMenu.containerViewChild?.nativeElement.style.display === "block")
    ) {
      return;
    }

    switch (event.code) {
      //down arrow
      case "ArrowDown":
        this.onArrowDown(event);
        break;

      //up arrow
      case "ArrowUp":
        this.onArrowUp(event);
        break;

      //right arrow
      case "ArrowRight":
        this.onArrowRight(event);
        break;

      //left arrow
      case "ArrowLeft":
        this.onArrowLeft(event);
        break;

      //enter
      case "Enter":
      case "NumpadEnter":
        this.onEnter(event);
        break;

      //space
      case "Space":
        {
          const nodeName =
            event.target instanceof HTMLElement && event.target.nodeName;
          if (!nodeName || !["INPUT"].includes(nodeName)) {
            this.onEnter(event);
          }
        }
        break;

      //tab
      case "Tab":
        if (!this.ignoreDefaultTabOrder) {
          this.setAllNodesTabIndexes();

          if (event.shiftKey) {
            this.onArrowUp(event);
          } else {
            this.onArrowDown(event);
          }
        }
        break;

      default:
        //no op
        break;
    }
  }

  onArrowUp(event: KeyboardEvent) {
    const target = event.target as HTMLElement;
    const nodeElement = findPreviousElement(target, "p-treeNode");
    if (!nodeElement) {
      return;
    }

    this.focusRowChange(nodeElement, nodeElement);

    event.preventDefault();
  }

  onArrowDown(event: KeyboardEvent) {
    const target = event.target as HTMLElement;
    const nodeElement = findNextElement(target, "p-treeNode");
    if (!nodeElement) {
      return;
    }

    this.focusRowChange(nodeElement, nodeElement);

    event.preventDefault();
  }

  onArrowRight(event: KeyboardEvent) {
    if (!this.node().expanded && !this.isNodeLeaf()) {
      if (globalThis.__rtl) {
        this.collapse(event);
      } else {
        this.expand(event);
      }
    }

    event.preventDefault();
  }

  onArrowLeft(event: KeyboardEvent) {
    if (this.node().expanded && !this.isNodeLeaf()) {
      if (globalThis.__rtl) {
        this.expand(event);
      } else {
        this.collapse(event);
      }
    }

    event.preventDefault();
  }

  isActionableElement(event: Event) {
    const target = event.target;
    const isActionable =
      target instanceof HTMLElement &&
      (target.nodeName == "A" || target.nodeName == "BUTTON");

    return isActionable;
  }

  onEnter(event: KeyboardEvent) {
    this.tree.onNodeClick(event, this.node());
    this.setTabIndexForSelectionMode(event, this.tree.nodeTouched);

    if (!this.isActionableElement(event)) {
      event.preventDefault();
    }

    // Toggle node if it is not a leaf
    if (!this.isNodeLeaf()) {
      this.toggle(event);
    }
  }

  setAllNodesTabIndexes() {
    if (!this.ignoreDefaultTabOrder) {
      const nodes = find(this.tree.el.nativeElement, ".p-treenode");

      const hasSelectedNode = nodes.some(
        (node) =>
          node.getAttribute("aria-selected") === "true" ||
          node.getAttribute("aria-checked") === "true",
      );

      nodes.forEach((node) => {
        node.tabIndex = -1;
      });

      if (hasSelectedNode) {
        const selectedNode = nodes.find(
          (node) =>
            node.getAttribute("aria-selected") === "true" ||
            node.getAttribute("aria-checked") === "true",
        );

        invariant(selectedNode);
        selectedNode.tabIndex = 0;

        return;
      }
      invariant(nodes[0]);
      nodes[0].tabIndex = 0;
    }
  }

  setTabIndexForSelectionMode(
    event: Event,
    nodeTouched: boolean | null | undefined,
  ) {
    if (this.tree.selectionMode() && !this.ignoreDefaultTabOrder) {
      const elements = [...find(this.tree.el.nativeElement, ".p-treenode")];

      const currentTarget = event.currentTarget as HTMLElement | undefined;
      invariant(currentTarget);
      currentTarget.tabIndex = nodeTouched === false ? -1 : 0;

      if (elements.every((element) => element.tabIndex === -1)) {
        invariant(elements[0]);
        elements[0].tabIndex = 0;
      }
    }
  }

  private findNextSiblingOfAncestor(
    nodeElement: HTMLElement,
  ): HTMLElement | null {
    const parentNodeElement = this.getParentNodeElement(nodeElement);
    if (parentNodeElement) {
      if (parentNodeElement.nextElementSibling) {
        return parentNodeElement.nextElementSibling as HTMLElement;
      }

      return this.findNextSiblingOfAncestor(parentNodeElement);
    }

    return null;
  }

  findLastVisibleDescendant(
    nodeElement: HTMLElement | undefined,
  ): HTMLElement | undefined {
    if (!nodeElement) {
      return undefined;
    }

    const listElement = Array.from(nodeElement.children).find((el) =>
      hasClass(el as HTMLElement, "p-treenode"),
    );
    invariant(listElement);
    const childrenListElement = listElement.children[1];
    if (childrenListElement && childrenListElement.children.length > 0) {
      const lastChildElement = childrenListElement.children[
        childrenListElement.children.length - 1
      ] as HTMLElement;

      return this.findLastVisibleDescendant(lastChildElement);
    } else {
      return nodeElement;
    }
  }

  private getParentNodeElement(
    nodeElement: HTMLElement | Element | null | undefined,
  ) {
    const parentNodeElement =
      nodeElement?.parentElement?.parentElement?.parentElement;

    return parentNodeElement?.tagName === "P-TREENODE"
      ? parentNodeElement
      : null;
  }

  private focusNode(element: HTMLElement) {
    if (element.children.length > 0) {
      (element.children[0] as HTMLElement | undefined)?.focus();
    } else {
      element.focus();
    }
  }

  focusRowChange(
    firstFocusableRow: HTMLElement,
    currentFocusedRow: HTMLElement,
    lastVisibleDescendant?: HTMLElement,
  ) {
    if (!this.ignoreDefaultTabOrder) {
      firstFocusableRow.tabIndex = -1;

      const firstChild = currentFocusedRow.children[0] as
        | HTMLElement
        | undefined;
      if (firstChild) {
        firstChild.tabIndex = 0;
      }

      this.focusNode(lastVisibleDescendant ?? currentFocusedRow);
    }
  }

  focusVirtualNode() {
    yieldToMainThread(() => {
      const node = findSingle(
        document.body,
        `[data-id="${(this.node().key as TreeNode | undefined) ?? (this.node().data as TreeNode)}"]`,
      );
      focus(node);
    });
  }
}
